import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const AuthContext = createContext(null);

const LOCAL_DEV_KEY = "almoune_local_dev_user";

const getLocalDevUser = () => {
  try {
    const raw = localStorage.getItem(LOCAL_DEV_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setLocalDevUser = (user) => {
  if (user) {
    localStorage.setItem(LOCAL_DEV_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(LOCAL_DEV_KEY);
  }
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId) => {
    if (!isSupabaseConfigured || !supabase || !userId) {
      setProfile(null);
      return null;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, role, phone, is_active")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      setProfile(null);
      return null;
    }

    setProfile(data || null);
    return data || null;
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      const localUser = getLocalDevUser();
      if (localUser) {
        setSession({ user: localUser });
        setProfile(localUser.profile);
      }
      setLoading(false);
      return undefined;
    }

    let isMounted = true;

    const initAuth = async () => {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();

      if (!isMounted) {
        return;
      }

      setSession(currentSession);
      if (currentSession?.user?.id) {
        await loadProfile(currentSession.user.id);
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      setSession(nextSession);
      if (nextSession?.user?.id) {
        await loadProfile(nextSession.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value = useMemo(
    () => ({
      session,
      user: session?.user || null,
      profile,
      role: profile?.role || null,
      loading,
      isSupabaseConfigured,
      refreshProfile: async () => {
        if (!session?.user?.id) {
          setProfile(null);
          return null;
        }
        return loadProfile(session.user.id);
      },
      signOut: async () => {
        if (!supabase) {
          setSession(null);
          setProfile(null);
          setLocalDevUser(null);
          return;
        }
        await supabase.auth.signOut();
        setSession(null);
        setProfile(null);
      },
    }),
    [loading, profile, session, loadProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
